import React from 'react';
import PropTypes from 'prop-types';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  CircularProgress,
} from '@material-ui/core';

import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import * as userSelectors from 'selectors/user';
import {
  scaPreEnroll,
  scaStartEnrollment,
  scaStartRecipientEnrollment,
  scaTriggerOff,
  fetchUserSelfRequest,
} from 'actions';

import { sanitizeReturnUrl } from 'logic/sca';
import { useSCAVerification } from 'hooks/useSCAVerification';
import SCAInformation from './SCAInformation';

function getSCAVerificationMessage(status, result, error, preEnroll, recipientEnroll) {
  if (status === 'expired') {
    return {
      title: (
        <FormattedMessage id="sca.expired.heading" defaultMessage="Verification Link Expired" />
      ),
      content: (
        <FormattedMessage
          id="sca.expired.text"
          defaultMessage="Your verification link has expired. Please start the process again."
        />
      ),
    };
  }

  if (status === 'success') {
    return {
      title: (
        <FormattedMessage id="sca.success.heading" defaultMessage="SCA Verification Successful" />
      ),
      content: (
        <FormattedMessage
          id="sca.success.text"
          defaultMessage="Welcome back! Your SCA verification was successful."
        />
      ),
    };
  }
  const controlStatus = result?.controlStatus;
  if (status === 'error') {
    let message = (
      <FormattedMessage
        id="sca.failed.text"
        defaultMessage="Unfortunately, the verification was not successful."
      />
    );

    if (controlStatus === 'FAILED') {
      message = (
        <FormattedMessage
          id="sca.session.expired"
          defaultMessage="Your SCA session failed or expired. Please try again."
        />
      );
    } else if (error) {
      message = error;
    } else {
      message = (
        <FormattedMessage
          id="sca.error.2"
          defaultMessage="Something went wrong during the verification process."
        />
      );
    }

    return {
      title: <FormattedMessage id="sca.failed.heading" defaultMessage="SCA Verification Failed" />,
      content: message,
    };
  }

  if (status === 'invalid') {
    let title = (
      <FormattedMessage id="sca.failed.heading" defaultMessage="SCA Verification Invalid" />
    );
    let message = (
      <FormattedMessage
        id="sca.error.2"
        defaultMessage="Your verification result could not be processed. Please try again."
      />
    );

    // Handle all other controlStatus codes that might appear from nowhere
    if (controlStatus !== 'FAILED') {
      title = (
        <FormattedMessage id="sca.general.success.title" defaultMessage="Verification Successful" />
      );
      message = (
        <FormattedMessage
          id="sca.general.success.message"
          defaultMessage="Your verification was processed and saved."
        />
      );
    }
    return {
      title,
      content: message,
    };
  }

  if (preEnroll || recipientEnroll) {
    return {
      title: (
        <FormattedMessage
          id="sca.information.heading"
          defaultMessage="Prepare for Identity Verification"
        />
      ),
      content: <SCAInformation showTitle={false} />,
    };
  }

  return {
    title: <FormattedMessage id="sca.enrollment.title" defaultMessage="SCA Verification" />,
    content: '',
  };
}

const SCAVerificationDialog = ({ returnUrl }) => {
  const dispatch = useDispatch();
  const { verification, showDialog, clearSCAFlowState, status, actionUrl, expired } =
    useSCAVerification();

  const updateState = useSelector(userSelectors.updateState);
  const triggerOn = useSelector((state) => state.scaVerification.triggerOn);

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const mountedRef = React.useRef(true);

  const resolvedReturnUrl = React.useRef(sanitizeReturnUrl(returnUrl ?? window.location.href));
  const effectiveStatus = expired ? 'expired' : status;

  const { result, error, preEnroll, recipientEnroll, rid: reduxRid } = verification;

  const { title, content: message } = getSCAVerificationMessage(
    effectiveStatus,
    result,
    error,
    preEnroll,
    recipientEnroll,
  );

  const safeSetDialogOpen = (val) => {
    if (mountedRef.current) setDialogOpen(val);
  };

  const handleClose = async () => {
    safeSetDialogOpen(false);
    // Ensure SCA state + URL are cleaned before we fetch the fresh user snapshot
    await clearSCAFlowState();

    const shouldRefetch = preEnroll || recipientEnroll || expired;
    if (shouldRefetch) {
      dispatch(fetchUserSelfRequest());
    }
  };

  const handleContinue = () => {
    safeSetDialogOpen(false);

    if (preEnroll) {
      dispatch(
        scaStartEnrollment({
          returnUrl: resolvedReturnUrl.current,
        }),
      );
    } else if (recipientEnroll && actionUrl) {
      dispatch(
        scaStartRecipientEnrollment({
          actionUrl,
          returnUrl: resolvedReturnUrl.current,
          rid: reduxRid,
        }),
      );
    }
  };

  // Track mount status
  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Dialog opening
  React.useEffect(() => {
    if (showDialog && !dialogOpen && mountedRef.current) {
      setDialogOpen(true);
    }
  }, [showDialog, dialogOpen]);

  // SCA trigger cleanup
  React.useEffect(() => {
    if (updateState === 2 && triggerOn) {
      dispatch(scaPreEnroll());
      dispatch(scaTriggerOff());
    }

    if (updateState === 3 && triggerOn) {
      dispatch(scaTriggerOff());
    }
  }, [updateState, triggerOn, dispatch]);

  if (!showDialog && status !== 'loading') return null;

  const content =
    effectiveStatus === 'loading' ? (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <CircularProgress />
        <Typography variant="body2" style={{ marginTop: '1rem' }}>
          <FormattedMessage id="sca.verifying" defaultMessage="Verifying..." />
        </Typography>
      </div>
    ) : (
      <Typography gutterBottom component="div">
        {message}
      </Typography>
    );

  return (
    <Dialog open={dialogOpen} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>{content}</DialogContent>
      <DialogActions>
        {preEnroll || recipientEnroll ? (
          <>
            <Button
              onClick={handleContinue}
              color="primary"
              variant="contained"
              disabled={effectiveStatus === 'expired'}
            >
              <FormattedMessage id="continue" defaultMessage="Continue" />
            </Button>
            <Button onClick={handleClose} color="default">
              <FormattedMessage id="cancel" defaultMessage="Cancel" />
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleClose} color="primary" autoFocus>
              <FormattedMessage id="close" defaultMessage="Close" />
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

SCAVerificationDialog.propTypes = {
  returnUrl: PropTypes.string,
};

export default SCAVerificationDialog;
